import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import RichTextEditor from '../../components/RichTextEditor.jsx';
import { DOC_KINDS } from './ws.jsx';
import { DocsNav, PdfFrame, pdfBlobUrl, PlaceholderChips, StampImage, usePrivateImage } from './DocLibrary.jsx';
import { SigningPanel, SignaturePicture } from './Signatures.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// "Créer un document" (migration 033), 4 steps, a draft at any step:
//   1 template (by type and language) and customer
//   2 the values, prefilled from the customer / RFQ / chat (blanks highlighted) + items table
//   3 the generated text, editable for this document only
//   4 the PDF with the letterhead and the reference
// Started from: Documents → New, a customer, a quote request (?rfqId=), a
// chat conversation (?convId=), or a template (?template=). /:id/edit
// opens an existing draft.

// Form fields → the placeholder each one fills.
export const FIELDS = [
  ['customerCompany', 'customer_company'], ['customerContact', 'customer_name'], ['customerEmail', 'customer_email'], ['customerAddress', 'customer_address', 'full'],
  ['product', 'product', 'full'], ['specs', 'specs', 'full', 'area'], ['quantity', 'quantity'], ['unit', 'unit'], ['price', 'price'], ['currency', 'currency'],
  ['incoterm', 'incoterm'], ['port', 'port'], ['paymentTerms', 'payment_terms'], ['validity', 'validity', '', 'date'], ['delivery', 'delivery_time', 'full'],
  ['notes', 'notes', 'full', 'area'],
];
// The signatory isn't typed: it is the signature record chosen (SignatoryPick).

// "Produit et Valide jusqu'au sont vides" / "the items table is empty" — or null.
export function emptyWarning(doc, t) {
  const w = doc?.warnings;
  if (!w) return '';
  const byPh = Object.fromEntries(FIELDS.map(([k, ph]) => [ph, k]));
  const names = (w.empty || []).map((ph) => (byPh[ph] ? t(`ws.docf.${byPh[ph]}`) : ph.replace(/_/g, ' ')));
  const parts = [];
  if (names.length) parts.push(t(names.length === 1 ? 'ws.dw.emptyOne' : 'ws.dw.emptyMany', { fields: names.join(', ') }));
  if (w.itemsEmpty) parts.push(t('ws.dw.itemsEmpty'));
  return parts.join(' ');
}

// Details step: who signs — one of the signature records (mine by default;
// others only with "Sign for others"); name, title and picture come together.
function SignatoryPick({ doc, onDoc, disabled }) {
  const { t, errorText } = useI18n();
  const [sigs, setSigs] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/workspace/doc-signatories').then(setSigs).catch(() => setSigs({ signatories: [] })); }, []);
  if (!doc || !sigs) return null;
  const sg = doc.signing || { blocks: [], layout: 'row', align: 'left' };
  const first = sg.blocks[0] || null;
  const cur = first?.signatoryId ? String(first.signatoryId) : first?.hand ? 'hand' : '';
  const chosen = sigs.signatories.find((s) => String(s.id) === cur);
  const mine = sigs.signatories.find((s) => s.mine);
  const block1 = doc.signatures?.find((x) => x.slot === 1);
  async function pick(v) {
    setError('');
    const b = v === 'hand' ? { hand: true } : { signatoryId: Number(v) };
    const blocks = [b, ...sg.blocks.slice(1)];
    try { onDoc(await api(`/workspace/documents/${doc.id}`, { method: 'PATCH', body: { signing: { ...sg, blocks } } })); } catch (e) { setError(errorText(e)); }
  }
  return (
    <div className="field ws-full ws-signatory-pick">
      <span className="field-label">{t('ws.docf.signatory')}</span>
      <div className="ws-row">
        {chosen?.hasPicture && <SignaturePicture id={chosen.id} version={chosen.updatedAt} className="sig-img" />}
        <select value={cur} disabled={disabled} onChange={(e) => pick(e.target.value)} aria-label={t('ws.docf.signatory')}>
          {!cur && <option value="">—</option>}
          {sigs.signatories.filter((s) => s.mine || s.canUse).map((s) => <option key={s.id} value={s.id}>{s.fullName}{s.title ? ` — ${s.title}` : ''}{s.mine ? ` (${t('sig.me')})` : ''}</option>)}
          <option value="hand">{t('sig.optHand')}</option>
        </select>
      </div>
      <span className="field-hint">{chosen ? `${chosen.fullName}${chosen.title ? ` · ${chosen.title}` : ''}` : ''}{block1?.status === 'pending' ? ` — ${t('sig.waiting', { names: block1.name })}` : ''}</span>
      {!mine && <span className="field-hint">{t('sig.noOwnHint')} <Link to="/account/signature">{t('sig.myTitle')}</Link></span>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
const num = (v) => { const n = Number(String(v ?? '').replace(/\s/g, '').replace(',', '.')); return Number.isFinite(n) ? n : null; };
export function totalsOf(items) {
  const rows = items?.rows || [];
  const priced = rows.some((r) => String(r.unitPrice ?? '').trim() !== '' && num(r.unitPrice) !== null);
  const lines = rows.map((r) => (num(r.qty) !== null && String(r.unitPrice ?? '').trim() !== '' && num(r.unitPrice) !== null ? num(r.qty) * num(r.unitPrice) : null));
  const subtotal = lines.reduce((s, x) => s + (x || 0), 0);
  const rate = num(items?.taxRate) || 0;
  const tax = Math.round(subtotal * rate) / 100;
  return { priced, lines, subtotal, tax, total: subtotal + tax };
}
export const money = (n, lang) => new Intl.NumberFormat(lang === 'en' ? 'en-CA' : 'fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// Items table editor (product, specs, qty, unit, unit price, total; subtotal / tax / total).
export function ItemsEditor({ items, onChange, lang }) {
  const { t } = useI18n();
  const rows = items.rows || [];
  const tot = totalsOf(items);
  const setRow = (i, k, v) => onChange({ ...items, rows: rows.map((r, n) => (n === i ? { ...r, [k]: v } : r)) });
  return (
    <div className="ws-items">
      <div className="ws-items-head">
        <h3>{t('ws.dw.items')}</h3>
        <label className="field ws-items-cur"><span className="field-label">{t('ws.docf.currency')}</span><input value={items.currency || ''} onChange={(e) => onChange({ ...items, currency: e.target.value.toUpperCase().slice(0, 10) })} /></label>
      </div>
      <div className="ws-table-wrap">
        <table className="ws-table ws-items-table ws-table-cards">
          <thead><tr><th>{t('ws.dw.it.product')}</th><th>{t('ws.dw.it.specs')}</th><th>{t('ws.dw.it.qty')}</th><th>{t('ws.dw.it.unit')}</th><th>{t('ws.dw.it.unitPrice')}</th><th>{t('ws.dw.it.total')}</th><th aria-label={t('ws.dw.it.remove')} /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td data-label={t('ws.dw.it.product')}><input value={r.product || ''} onChange={(e) => setRow(i, 'product', e.target.value)} aria-label={t('ws.dw.it.product')} /></td>
                <td data-label={t('ws.dw.it.specs')}><input value={r.specs || ''} onChange={(e) => setRow(i, 'specs', e.target.value)} aria-label={t('ws.dw.it.specs')} /></td>
                <td data-label={t('ws.dw.it.qty')}><input inputMode="decimal" value={r.qty || ''} onChange={(e) => setRow(i, 'qty', e.target.value)} aria-label={t('ws.dw.it.qty')} className="ws-num" /></td>
                <td data-label={t('ws.dw.it.unit')}><input value={r.unit || ''} onChange={(e) => setRow(i, 'unit', e.target.value)} aria-label={t('ws.dw.it.unit')} /></td>
                <td data-label={t('ws.dw.it.unitPrice')}><input inputMode="decimal" value={r.unitPrice || ''} onChange={(e) => setRow(i, 'unitPrice', e.target.value)} aria-label={t('ws.dw.it.unitPrice')} className="ws-num" /></td>
                <td data-label={t('ws.dw.it.total')} className="ws-num">{tot.lines[i] === null ? '—' : money(tot.lines[i], lang)}</td>
                <td><button type="button" className="ws-x" aria-label={t('ws.dw.it.remove')} onClick={() => onChange({ ...items, rows: rows.filter((_, n) => n !== i) })}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange({ ...items, rows: [...rows, { product: '', specs: '', qty: '', unit: '', unitPrice: '' }] })}>+ {t('ws.dw.it.add')}</button>
      <div className="ws-items-tax">
        <label className="field"><span className="field-label">{t('ws.dw.taxLabel')}</span><input value={items.taxLabel || ''} onChange={(e) => onChange({ ...items, taxLabel: e.target.value })} placeholder={lang === 'en' ? 'GST+QST' : 'TPS+TVQ'} /></label>
        <label className="field"><span className="field-label">{t('ws.dw.taxRate')}</span><input inputMode="decimal" value={items.taxRate || ''} onChange={(e) => onChange({ ...items, taxRate: e.target.value })} placeholder="14.975" /></label>
        {tot.priced && (
          <dl className="ws-totals">
            <dt>{t('ws.dw.subtotal')}</dt><dd>{money(tot.subtotal, lang)} {items.currency}</dd>
            {tot.tax > 0 && <><dt>{items.taxLabel || t('ws.dw.tax')}</dt><dd>{money(tot.tax, lang)} {items.currency}</dd></>}
            <dt><strong>{t('ws.dw.total')}</strong></dt><dd><strong>{money(tot.total, lang)} {items.currency}</strong></dd>
          </dl>
        )}
      </div>
    </div>
  );
}

// "Mise en page" (step 4, migration 034): the top logo (letterhead's / this
// document's own / none, position, size) and the company stamp (which,
// position, size, rotation, opacity, every page). Each change is saved and
// the PDF preview is made again.
export function LayoutPanel({ doc, onDoc, onError }) {
  const { t, errorText } = useI18n();
  const [stamps, setStamps] = useState(null);
  const [busy, setBusy] = useState(false);
  const L = doc.layout || { logo: { mode: 'letterhead', size: 'medium' }, stamp: null };
  const logo = L.logo || { mode: 'letterhead', size: 'medium' };
  const ownLogo = usePrivateImage(logo.mode === 'custom' && logo.path ? `/workspace/documents/${doc.id}/logo?v=${encodeURIComponent(logo.path)}` : null);
  useEffect(() => { api('/workspace/doc-stamps').then((d) => setStamps(d.stamps)).catch(() => setStamps([])); }, []);
  async function save(next) {
    setBusy(true);
    try { onDoc(await api(`/workspace/documents/${doc.id}`, { method: 'PATCH', body: { layout: next } })); }
    catch (e) { onError(errorText(e)); } finally { setBusy(false); }
  }
  async function saveSigning(next) {
    setBusy(true);
    try { onDoc(await api(`/workspace/documents/${doc.id}`, { method: 'PATCH', body: { signing: next } })); }
    catch (e) { onError(errorText(e)); } finally { setBusy(false); }
  }
  const nSig = doc.signing?.blocks?.length || 0;
  const setLogo = (k, v) => save({ ...L, logo: { ...logo, [k]: v } });
  const st = L.stamp;
  const setStamp = (k, v) => save({ ...L, stamp: { ...st, [k]: v } });
  async function upload(file) {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) { onError(t('errors.invalid_image')); return; }
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try { onDoc(await api(`/workspace/documents/${doc.id}/logo`, { method: 'POST', form: fd })); } catch (e) { onError(errorText(e)); } finally { setBusy(false); }
  }
  const editable = doc.canEdit;
  const radio = (name, value, cur, label, on) => (
    <label key={value} className="ws-pill"><input type="radio" name={name} checked={cur === value} disabled={!editable || busy} onChange={() => on(value)} /><span>{label}</span></label>
  );
  return (
    <section className="ws-card ws-layout" aria-busy={busy}>
      <h2>{t('ws.lay.title')}</h2>
      <fieldset className="ws-layout-group">
        <legend>{t('ws.lay.logo')}</legend>
        <div className="ws-pills">
          {radio('logo-mode', 'letterhead', logo.mode, t('ws.lay.logoLetterhead'), (v) => setLogo('mode', v))}
          {logo.path && radio('logo-mode', 'custom', logo.mode, t('ws.lay.logoCustom'), (v) => setLogo('mode', v))}
          {radio('logo-mode', 'none', logo.mode, t('ws.lay.logoNone'), (v) => setLogo('mode', v))}
        </div>
        {editable && (
          <div className="ws-row ws-own-logo">
            {ownLogo && logo.mode === 'custom' && <img src={ownLogo} alt="" />}
            <label className="btn btn-ghost btn-sm">{t(logo.path ? 'ws.lay.logoReplace' : 'ws.lay.logoUpload')}<input type="file" accept="image/png,image/jpeg" hidden onChange={(e) => upload(e.target.files[0])} /></label>
            <span className="ws-hint">{t('ws.lay.logoHint')}</span>
          </div>
        )}
        {logo.mode !== 'none' && (
          <div className="ws-layout-opts">
            <span className="field-label">{t('ws.lay.position')}</span>
            <div className="ws-pills">{['left', 'center', 'right'].map((v) => radio('logo-pos', v, logo.position || 'left', t(`ws.lay.pos.${v}`), (x) => setLogo('position', x)))}</div>
            <span className="field-label">{t('ws.lay.size')}</span>
            <div className="ws-pills">{['small', 'medium', 'large'].map((v) => radio('logo-size', v, logo.size || 'medium', t(`ws.lay.sz.${v}`), (x) => setLogo('size', x)))}</div>
          </div>
        )}
      </fieldset>

      <SigningPanel value={doc.signing || null} onChange={saveSigning} disabled={!editable || busy} />
      {doc.signatures?.some((s) => s.status === 'pending') && <p className="callout callout-info small">{t('sig.waiting', { names: doc.signatures.filter((s) => s.status === 'pending').map((s) => s.name).join(', ') })}</p>}
      <fieldset className="ws-layout-group">
        <legend>{t('ws.lay.stamp')}</legend>
        {!doc.canStamp ? <p className="ws-hint">{st ? t('ws.lay.stampBy', { name: st.addedByName || '—', stamp: st.name }) : ''} {t('ws.lay.stampNoRight')}</p>
          : stamps && !stamps.length ? <p className="ws-hint">{t('ws.lay.noStamps')}</p> : stamps && (
            <>
              <label className="field-checkbox"><input type="checkbox" checked={!!st} disabled={!editable || busy} onChange={(e) => save({ ...L, stamp: e.target.checked ? { id: stamps[0].id, position: 'signature', size: 'medium', rotate: 0, opacity: 100, everyPage: false } : null })} /><span>{t('ws.lay.addStamp')}</span></label>
              {st && (
                <div className="ws-layout-opts">
                  <span className="field-label">{t('ws.lay.which')}</span>
                  <div className="ws-stamp-pick">
                    {stamps.map((x) => (
                      <label key={x.id} className={`ws-stamp-choice${st.id === x.id ? ' is-on' : ''}`}>
                        <input type="radio" name="stamp-id" checked={st.id === x.id} disabled={!editable || busy} onChange={() => setStamp('id', x.id)} />
                        <StampImage id={x.id} alt="" /><span>{x.name}</span>
                      </label>
                    ))}
                  </div>
                  <span className="field-label">{t('ws.lay.position')}</span>
                  <div className="ws-pills">{['signature', 'bottom_right', 'bottom_left', 'over_signature'].map((v) => radio('stamp-pos', v, st.position, t(`ws.lay.spos.${v}`), (x) => setStamp('position', x)))}</div>
                  {nSig > 1 && ['signature', 'over_signature'].includes(st.position) && (
                    <><span className="field-label">{t('sig.whichSignature')}</span>
                      <div className="ws-pills">{Array.from({ length: nSig }, (_, i) => radio('stamp-sig', i, st.sigIndex || 0, `${t('sig.block', { n: i + 1 })}`, (x) => setStamp('sigIndex', x)))}</div></>
                  )}
                  <span className="field-label">{t('ws.lay.size')}</span>
                  <div className="ws-pills">{['small', 'medium', 'large'].map((v) => radio('stamp-size', v, st.size, t(`ws.lay.sz.${v}`), (x) => setStamp('size', x)))}</div>
                  <span className="field-label">{t('ws.lay.rotate')}</span>
                  <div className="ws-pills">{[-8, 0, 8].map((v) => radio('stamp-rot', v, st.rotate, `${v > 0 ? '+' : ''}${v}°`, (x) => setStamp('rotate', x)))}</div>
                  <span className="field-label">{t('ws.lay.opacity')}</span>
                  <div className="ws-pills">{[100, 85, 70].map((v) => radio('stamp-op', v, st.opacity, `${v} %`, (x) => setStamp('opacity', x)))}</div>
                  <label className="field-checkbox"><input type="checkbox" checked={!!st.everyPage} disabled={!editable || busy} onChange={(e) => setStamp('everyPage', e.target.checked)} /><span>{t('ws.lay.everyPage')}</span></label>
                  {st.addedByName && <p className="ws-hint">{t('ws.lay.stampBy', { name: st.addedByName, stamp: st.name })}</p>}
                </div>
              )}
            </>
          )}
      </fieldset>
    </section>
  );
}

const STEP_KEYS = ['template', 'values', 'text', 'preview'];
const hasPlaceholders = (html) => /\{(?!items_table\})[a-z_]+\}/.test(html || '');

export default function DocWizard({ base = '/account/workspace' }) {
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [lib, setLib] = useState(null);
  const [ctx, setCtx] = useState(null);           // { crmId, customer, values, items, rfqId, convId, listingId }
  const [doc, setDoc] = useState(null);
  const [templateId, setTemplateId] = useState(params.get('template') || '');
  const [kind, setKind] = useState(params.get('kind') || '');
  const [tlang, setTlang] = useState(params.get('template') ? '' : lang);
  const [values, setValues] = useState({});
  const [items, setItems] = useState(null);
  const [html, setHtml] = useState('');
  const [dirty, setDirty] = useState({ values: false, text: false });
  const [textTouched, setTextTouched] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [custQ, setCustQ] = useState('');
  const [custs, setCusts] = useState([]);
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const [busy, setBusy] = useState('');
  const top = useRef(null);
  const say = (k, text) => setMsg({ kind: k, text });

  // Library, then the context (customer / RFQ / chat) or the existing draft.
  useEffect(() => {
    api('/workspace/doc-library').then(setLib).catch((e) => say('error', errorText(e)));
    if (routeId) {
      api(`/workspace/documents/${routeId}`).then((d) => {
        setDoc(d); setValues(d.data); setItems(d.items); setHtml(d.html); setTlang(d.lang); setKind(d.kind);
        setTemplateId(d.templateId ? String(d.templateId) : `starter:${d.kind}:${d.lang}`);
        setTextTouched(!hasPlaceholders(d.html)); setStep(Number(params.get('step')) || 2);
        setCtx({ crmId: d.crmId, customer: { company: d.customer } });
      }).catch((e) => say('error', errorText(e)));
      return;
    }
    const q = ['crmId', 'rfqId', 'convId'].map((k) => (params.get(k) ? `${k}=${encodeURIComponent(params.get(k))}` : '')).filter(Boolean).join('&');
    if (q) api(`/workspace/documents/context?${q}`).then((c) => { setCtx(c); setValues(c.values); if (c.items) setItems(c.items); }).catch((e) => say('error', errorText(e)));
  }, [routeId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Customer search (no context).
  useEffect(() => {
    if (ctx || routeId) return undefined;
    const tm = setTimeout(() => api(`/workspace/documents/customers?q=${encodeURIComponent(custQ)}`).then(setCusts).catch(() => {}), 250);
    return () => clearTimeout(tm);
  }, [custQ, ctx, routeId]);
  const pickCustomer = (crmId) => api(`/workspace/documents/context?crmId=${crmId}`).then((c) => { setCtx(c); setValues(c.values); }).catch((e) => say('error', errorText(e)));
  useEffect(() => { window.scrollTo(0, 0); }, [step]);
  // Opened at step 4 (…/edit?step=4): the PDF preview.
  useEffect(() => { if (step === 4 && doc && !pdf) pdfBlobUrl(`/workspace/documents/${doc.id}/pdf?fresh=1`).then(setPdf).catch(() => {}); }, [step, doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const all = useMemo(() => (lib ? [...lib.templates.filter((x) => !x.archived), ...lib.starters] : []), [lib]);
  const tpl = all.find((x) => String(x.id) === String(templateId)) || null;
  const shown = all.filter((x) => (!kind || x.kind === kind) && (!tlang || x.lang === tlang));
  // Placeholders the template uses → the fields to highlight when blank.
  const used = useMemo(() => new Set(((tpl?.bodyHtml || doc?.html || '').match(/\{([a-z_]+)\}/g) || []).map((x) => x.slice(1, -1))), [tpl, doc]);

  const setV = (k, v) => { setValues((x) => ({ ...x, [k]: v })); setDirty((d) => ({ ...d, values: true })); };
  const setIt = (v) => { setItems(v); setDirty((d) => ({ ...d, values: true })); };

  // Creates the draft (step 1 → 2) or saves what changed.
  async function ensureDoc() {
    if (doc) return doc;
    if (!ctx?.crmId) throw Object.assign(new Error('doc_pick_customer'), { code: 'doc_pick_customer' });
    if (!tpl) throw Object.assign(new Error('doc_pick_template'), { code: 'doc_pick_template' });
    const its = tpl.hasItems ? (items?.rows?.length ? items : { rows: [{ product: values.product || '', specs: values.specs || '', qty: values.quantity || '', unit: values.unit || '', unitPrice: values.price || '' }], currency: values.currency || 'USD' }) : null;
    const d = await api('/workspace/documents', { method: 'POST', body: { crmId: ctx.crmId, templateId: tpl.id, lang: tpl.lang, data: values, items: its, rfqId: ctx.rfqId, convId: ctx.convId, listingId: ctx.listingId } });
    setDoc(d); setItems(d.items); setHtml(d.html);
    window.history.replaceState(window.history.state, '', `${base}/documents/${d.id}/edit`);
    return d;
  }
  async function saveNow(d) {
    const body = {};
    if (dirty.values) { body.data = values; if (d.items !== null || items) body.items = items; }
    if (dirty.text) body.bodyHtml = html;
    if (!Object.keys(body).length) return d;
    const r = await api(`/workspace/documents/${d.id}`, { method: 'PATCH', body });
    setDoc(r); setDirty({ values: false, text: false });
    return r;
  }
  async function go(next) {
    setBusy('next'); say('', '');
    try {
      let d = await ensureDoc();
      // A different template chosen for an existing draft.
      if (step === 1 && tpl && String(d.templateId || `starter:${d.kind}:${d.lang}`) !== String(tpl.id)) {
        d = await api(`/workspace/documents/${d.id}/generate`, { method: 'POST', body: { templateId: tpl.id } }); setDoc(d); setItems(d.items); setTextTouched(false);
      }
      const valuesChanged = dirty.values;
      d = await saveNow(d);
      if (next === 3) {
        // The text from the template and the values (asked first if edited by hand).
        if (!textTouched || hasPlaceholders(d.html) || (valuesChanged && window.confirm(t('ws.dw.regenerate')))) {
          d = await api(`/workspace/documents/${d.id}/generate`, { method: 'POST', body: { reset: true } });
          setDoc(d);
        }
        setHtml(d.html); setTextTouched(true);
      }
      if (next === 4) { setPdf(null); setPdf(await pdfBlobUrl(`/workspace/documents/${d.id}/pdf?fresh=1`)); }
      setStep(next);
    } catch (e) { say('error', errorText(e)); } finally { setBusy(''); }
  }
  async function saveDraft() {
    setBusy('draft'); say('', '');
    try { const d = await saveNow(await ensureDoc()); say('success', t('ws.dw.draftSaved', { ref: d.ref })); } catch (e) { say('error', errorText(e)); } finally { setBusy(''); }
  }
  const finish = useCallback(() => navigate(`${base}/documents/${doc.id}${doc.status === 'draft' ? '?send=1' : ''}`), [navigate, base, doc]);

  if (!lib || (routeId && !doc)) return <div className="ws-page"><DocsNav base={base} /><Alert kind={msg.kind || 'error'}>{msg.text}</Alert>{!msg.text && <p className="muted">{t('common.loading')}</p>}</div>;
  const blank = (k, ph) => used.has(ph) && !String(values[k] ?? '').trim();
  return (
    <div className="ws-page" ref={top}>
      <DocsNav base={base} />
      <div className="acc-head"><TutorialSlot place={`ws.docs.new.s${step}`} />
        <div>
          <h1>{doc ? `${doc.title} — ${doc.ref}` : t('ws.dw.title')}</h1>
          <p className="muted">{ctx?.customer?.company ? t('ws.dw.for', { company: ctx.customer.company }) : t('ws.dw.lead')}</p>
        </div>
        <div className="acc-head-actions"><button type="button" className="btn btn-ghost" disabled={!!busy || (!doc && (!ctx?.crmId || !tpl))} onClick={saveDraft}>{busy === 'draft' ? t('common.saving') : t('ws.dw.saveDraft')}</button></div>
      </div>
      <ol className="ws-steps ws-wizard-steps">
        {STEP_KEYS.map((k, i) => (
          <li key={k} className={i + 1 < step ? 'done' : i + 1 === step ? 'now' : ''}>
            {doc && i + 1 !== step ? <button type="button" className="btn-link" disabled={!!busy} onClick={() => go(i + 1)}>{i + 1}. {t(`ws.dw.step.${k}`)}</button> : <span>{i + 1}. {t(`ws.dw.step.${k}`)}</span>}
          </li>
        ))}
      </ol>
      <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>

      {step === 1 && (
        <section className="ws-card">
          {!ctx?.crmId && (
            <div className="ws-form">
              <label className="field ws-full"><span className="field-label">{t('ws.dw.customer')}</span>
                <input value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder={t('ws.dw.customerSearch')} /></label>
              <ul className="ws-list ws-full">{custs.map((c) => <li key={c.id}><button type="button" className="btn btn-link" onClick={() => pickCustomer(c.id)}>{c.name}</button></li>)}</ul>
              {!custs.length && <p className="muted small ws-full">{t('ws.dw.noCustomers')}</p>}
            </div>
          )}
          {ctx?.crmId && <p className="small">{t('ws.dw.customer')} : <strong>{ctx.customer.company}</strong>{ctx.rfqId ? ` · ${t('ws.dw.fromRfq')}` : ''}{ctx.convId ? ` · ${t('ws.dw.fromChat')}` : ''}</p>}
          <div className="acc-toolbar">
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('ws.docs.kind')}><option value="">{t('ws.docs.allKinds')}</option>{DOC_KINDS.map((k) => <option key={k} value={k}>{t(`ws.kind.${k}`)}</option>)}</select>
            <select value={tlang} onChange={(e) => setTlang(e.target.value)} aria-label={t('ws.docs.lang')}><option value="">{t('ws.docs.allLangs')}</option><option value="fr">FR</option><option value="en">EN</option></select>
          </div>
          <ul className="ws-tpl-pick" role="radiogroup" aria-label={t('ws.dw.step.template')}>
            {shown.map((x) => (
              <li key={x.id}>
                <label className={`ws-tpl-card${String(templateId) === String(x.id) ? ' is-on' : ''}`}>
                  <input type="radio" name="tpl" checked={String(templateId) === String(x.id)} onChange={() => setTemplateId(String(x.id))} />
                  <strong>{x.title}</strong>
                  <span className="muted small">{t(`ws.kind.${x.kind}`)} · {x.lang.toUpperCase()}{x.starter ? ` · ${t('ws.dw.starter')}` : ''}{x.hasItems ? ` · ${t('ws.lib.withItems')}` : ''}</span>
                </label>
              </li>
            ))}
          </ul>
          {!shown.length && <p className="muted small">{t('ws.lib.none')}</p>}
          <div className="ws-actions"><button type="button" className="btn btn-primary" disabled={!!busy || !tpl || !ctx?.crmId} onClick={() => go(2)}>{t('ws.dw.next')} →</button></div>
        </section>
      )}

      {step === 2 && (
        <section className="ws-card">
          <p className="ws-hint">{t('ws.dw.valuesHint')}</p>
          {FIELDS.some(([k, ph]) => blank(k, ph)) && <p className="callout callout-warning small" role="status">⚠ {t('ws.dw.emptyMany', { fields: FIELDS.filter(([k, ph]) => blank(k, ph)).map(([k]) => t(`ws.docf.${k}`)).join(', ') })} {t('ws.dw.emptyHint')}</p>}
          <div className="ws-form">
            <SignatoryPick doc={doc} onDoc={setDoc} disabled={!!busy} />
            {FIELDS.map(([k, ph, full, type]) => (
              <label key={k} className={`field${full ? ' ws-full' : ''}${blank(k, ph) ? ' is-blank' : ''}`}><span className="field-label">{t(`ws.docf.${k}`)}{blank(k, ph) && <span className="ws-blank-tag"> — {t('ws.dw.blank')}</span>}</span>
                {type === 'area' ? <textarea rows={3} value={values[k] || ''} onChange={(e) => setV(k, e.target.value)} />
                  : <input type={type === 'date' ? 'date' : 'text'} value={values[k] || ''} onChange={(e) => setV(k, e.target.value)} />}
              </label>
            ))}
          </div>
          {(doc?.items || items) && (doc ? doc.items !== null : tpl?.hasItems) && <ItemsEditor items={items || { rows: [], currency: values.currency || 'USD' }} onChange={setIt} lang={doc?.lang || tlang || lang} />}
          <div className="ws-actions">
            <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => go(1)}>← {t('ws.dw.back')}</button>
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => go(3)}>{t('ws.dw.next')} →</button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="ws-card">
          <p className="ws-hint">{t('ws.dw.textHint')}</p>
          <PlaceholderChips list={['items_table']} />
          <RichTextEditor id="doc-body" full docs docLang={doc?.lang || tlang || lang} value={html} onChange={(v) => { setHtml(v); setDirty((d) => ({ ...d, text: true })); }} onError={(c) => say('error', t(`errors.${c}`))} />
          <div className="ws-actions">
            <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => go(2)}>← {t('ws.dw.back')}</button>
            <button type="button" className="btn btn-link" disabled={!!busy} onClick={async () => { if (!window.confirm(t('ws.dw.resetConfirm'))) return; setBusy('reset'); try { const d = await api(`/workspace/documents/${doc.id}/generate`, { method: 'POST', body: { reset: true } }); setDoc(d); setHtml(d.html); setDirty((x) => ({ ...x, text: false })); } catch (e) { say('error', errorText(e)); } finally { setBusy(''); } }}>{t('ws.dw.reset')}</button>
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => go(4)}>{busy === 'next' ? t('common.loading') : `${t('ws.dw.next')} →`}</button>
          </div>
        </section>
      )}

      {step === 4 && doc && (
        <div className="ws-step4">
        <LayoutPanel doc={doc} onError={(m) => say('error', m)} onDoc={async (d) => { setDoc(d); say('', ''); setPdf(await pdfBlobUrl(`/workspace/documents/${d.id}/pdf?fresh=1`).catch(() => null)); }} />
        <section className="ws-card">
          <p className="small">{t('ws.docs.ref')} : <strong>{doc.ref}</strong>{doc.version > 1 ? ` · v${doc.version}` : ''}</p>
          {pdf ? <PdfFrame url={pdf} title={doc.ref} /> : <p className="muted">{t('common.loading')}</p>}
          <div className="ws-actions">
            <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => go(3)}>← {t('ws.dw.back')}</button>
            <Link className="btn btn-ghost" to={`${base}/documents/${doc.id}`}>{t('ws.dw.done')}</Link>
            <button type="button" className="btn btn-primary" onClick={finish}>{t('ws.dw.toSend')} →</button>
          </div>
        </section>
        </div>
      )}
    </div>
  );
}
